import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { MD_BREAKPOINT } from 'webapp/utils/common';

import { containerEventName } from 'webapp/modifiers/visible-check-container';
import type {
	BoardSelectListItem,
	BoardSelectListSelection,
} from 'webapp/components/boards/popup-select-list';

import type FilterSortBoardAtSelectPopup from 'webapp/models-custom/filter-sort/board-at-select-popup';
import type FilterSortBoardFromAnotherSpaceAtSelectPopup from 'webapp/models-custom/filter-sort/board-from-another-space-at-select-popup';
import type FilterSortReportAtSelectPopup from 'webapp/models-custom/filter-sort/report-at-select-popup';

export enum FilterSortType {
	BOARD = 'board',
	BOARD_FROM_ANOTHER_SPACE = 'boardFromAnotherSpace',
	REPORT = 'report',
}

export interface Args {
	model: BoardSelectListItem[];
	selected: BoardSelectListSelection;
	checkIfAllItemsCanBeSelected: (items: BoardSelectListItem[]) => boolean;
	checkIfItemCanBeSelected: (item: BoardSelectListItem) => boolean;
	onSelect: (item: BoardSelectListItem) => void;
	onSelectAll: (items: BoardSelectListItem[]) => void;
	onDeselectAll: () => void;
	onOpenView: (viewId: string) => void;
	onReposition: () => void;
	filterSortType?: FilterSortType;
}

export default class BoardsSelectBaseComponent<T extends Args> extends Component<T> {
	@tracked showJoinedFilterSwitchers = false;

	filterSort:
		| FilterSortBoardAtSelectPopup
		| FilterSortBoardFromAnotherSpaceAtSelectPopup
		| FilterSortReportAtSelectPopup
		| null = null;
	visibleCheckContainerNode: HTMLElement | null = null;

	constructor(owner: Owner, args: T) {
		super(owner, args);

		if (this.checkMobileView()) {
			this.showJoinedFilterSwitchers = true;
		}

		window.addEventListener('resize', this.debounceChangeShowJoinedFilterSwitchers);
	}

	get results() {
		return (this.filterSort?.results || []) as BoardSelectListItem[];
	}

	get isSelectedAll(): boolean {
		const { boards, boardLoops } = this.args.selected;

		return boards.length + boardLoops.length === this.results.length;
	}

	get hideDatasourceColumn(): boolean {
		return this.args.filterSortType === FilterSortType.REPORT;
	}

	checkMobileView(): boolean {
		return window.innerWidth < MD_BREAKPOINT;
	}

	changeShowJoinedFilterSwitchers(): void {
		this.showJoinedFilterSwitchers = false;

		if (this.checkMobileView()) {
			this.showJoinedFilterSwitchers = true;
		}
	}

	@action
	debounceChangeShowJoinedFilterSwitchers(): void {
		debounce(this, this.changeShowJoinedFilterSwitchers, 100);
	}

	@action
	selectAll(filteredItems: BoardSelectListItem[], value: boolean) {
		if (value) {
			this.args.onSelectAll(filteredItems);
		} else {
			this.args.onDeselectAll();
		}
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	setupView(node: HTMLElement): void {
		this.visibleCheckContainerNode = node;
	}

	@action
	teardownView(): void {
		window.removeEventListener('resize', this.debounceChangeShowJoinedFilterSwitchers);
	}
}
